import { Component, inject, OnInit, OnDestroy, signal, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { MatCardModule } from '@angular/material/card';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatDividerModule } from '@angular/material/divider';
import { MatChipsModule } from '@angular/material/chips';
import { MatTabsModule } from '@angular/material/tabs';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatToolbarModule } from '@angular/material/toolbar';
import { MatDialog } from '@angular/material/dialog';
import { Subject, takeUntil, switchMap, of, forkJoin } from 'rxjs';

import { CoiService } from '../../service/entity-services/coi.service';
import { Coi } from '../../objects/coi';
import { EntityType } from '../../objects/interface-library';
import { ViewEntity } from '../../objects/view-entity';
import { NewAttachmentComponent } from '../../create-new/new-attachment/new-attachment.component';
import { DocumentViewerSidenavComponent } from '../../entity-form-components/document-viewer-sidenav/document-viewer-sidenav.component';
import { EntityActionsSidenavComponent, EntityAction, EntityTab } from '../../entity-form-components/entity-actions-sidenav/entity-actions-sidenav.component';
import { VendorConnectionsComponent } from '../../entity-form-components/vendor-connections/vendor-connections.component';
import { AttachmentConnectionsComponent } from '../../entity-form-components/attachment-connections/attachment-connections.component';
import { CriticalDateConnectionsComponent } from '../../entity-form-components/critical-date-connections/critical-date-connections.component';
import { NotesComponent } from '../../entity-form-components/notes/notes.component';
import { VersionsComponent } from '../../entity-form-components/versions/versions.component';
import { AssessmentConnectionsComponent } from '../../entity-form-components/assessment-connections/assessment-connections.component';
import { WorkflowConnectionsComponent } from '../../entity-form-components/workflow-connections/workflow-connections.component';
import { ContractConnectionsComponent } from '../../entity-form-components/contract-connections/contract-connections.component';
import { PeopleTableComponent } from '../../entity-form-components/people-table/people-table.component';
import { NoteRecord } from '../../objects/note-record';

@Component({
  selector: 'app-certificate-of-insurance',
  standalone: true,
  imports: [
    CommonModule,
    MatCardModule,
    MatButtonModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatDividerModule,
    MatChipsModule,
    MatTabsModule,
    MatSidenavModule,
    MatToolbarModule,
    DocumentViewerSidenavComponent,
    EntityActionsSidenavComponent,
    VendorConnectionsComponent,
    AttachmentConnectionsComponent,
    CriticalDateConnectionsComponent,
    NotesComponent,
    VersionsComponent,
    AssessmentConnectionsComponent,
    WorkflowConnectionsComponent,
    ContractConnectionsComponent,
    PeopleTableComponent
  ],
  templateUrl: './certificate-of-insurance.component.html',
  styleUrls: ['./certificate-of-insurance.component.css']
})
export class CertificateOfInsuranceComponent extends ViewEntity implements OnInit, OnDestroy {
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  public coiService = inject(CoiService);
  private cdr = inject(ChangeDetectorRef);
  private destroy$ = new Subject<void>();

  currentCoi: Coi | null = null;
  loading = true;
  error: string | null = null;

  // Display-friendly values
  partyProvidingCoiName: string = '';
  insuredPartyName: string = '';
  insuranceProviderName: string = '';
  legalEntitiesDisplay: string = '';
  primaryOwnerName: string = '';
  coiCoordinatorName: string = '';
  internalSignatoryName: string = '';
  internalCoordinatorName: string = '';
  secondaryOwnerName: string = '';

  // Tab management
  selectedTabIndex = 0;

  // Sidenav actions using signals (Angular 20 pattern)
  sidenavActions = signal<EntityAction[]>([
    {
      label: 'Edit',
      icon: 'edit',
      action: () => this.editCoi(),
      disabled: false
    },
    {
      label: 'Add Attachment',
      icon: 'attach_file',
      action: () => this.addAttachment(),
      disabled: false
    },
    {
      label: 'New Important Date',
      icon: 'event',
      action: () => this.addCriticalDate(),
      disabled: false
    }
  ]);

  // Tabs configuration using signals
  sidenavTabs = signal<EntityTab[]>([
    { label: 'Associated Contracts', index: 0, icon: 'link' },
    { label: 'Attachments', index: 1, icon: 'attach_file' },
    { label: 'Dates', index: 2, icon: 'event' },
    { label: 'People', index: 3, icon: 'person' },
    { label: 'COI History', index: 4, icon: 'history' },
    { label: 'Provisions', index: 5, icon: 'gavel' },
    { label: 'Notes', index: 6, icon: 'note' },
    { label: 'Assessments', index: 7, icon: 'assessment' },
  ]);

  ngOnInit() {
    const coiId = this.route.snapshot.paramMap.get('id');

    if (!coiId) {
      this.error = 'No COI ID provided';
      this.loading = false;
      return;
    }

    this.loadCoiFromCache(coiId);
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadCoiFromCache(coiNum: string) {
    this.loading = true;
    this.error = null;

    // First, find the COI to get its coiId
    this.coiService.visibleCois$
      .pipe(
        takeUntil(this.destroy$),
        switchMap(cois => {
          const coi = cois.find(c => c.coiNum === coiNum);

          if (!coi) {
            if (cois.length === 0) {
              // COIs still loading - return empty to keep waiting
              return of(null);
            } else {
              // COI not found
              throw new Error('COI not found');
            }
          }

          // Now get the fully hydrated COI
          if (coi.coiId) {
            return this.coiService.getCoiObjectFullyHydrated(coi.coiId);
          } else {
            // If no coiId, return the COI as-is (shouldn't happen)
            return of(coi);
          }
        })
      )
      .subscribe({
        next: (hydratedCoi) => {
          if (hydratedCoi) {
            this.currentCoi = hydratedCoi;
            this.processCoiData();
            this.loading = false;
            this.cdr.detectChanges();
          }
          // If null, keep waiting
        },
        error: (err) => {
          console.error('Error loading COI:', err);
          this.error = err.message || 'Failed to load COI. Please try again.';
          this.loading = false;
          this.cdr.detectChanges();
        }
      });
  }

  processCoiData() {
    if (!this.currentCoi) return;

    // Extract display-friendly names
    this.partyProvidingCoiName = this.extractDisplayName(
      this.currentCoi.partyProvidingCoi,
      'name'
    );

    this.insuredPartyName = this.extractDisplayName(
      this.currentCoi.insuredParty,
      'name'
    );

    // Insurance provider can be an array
    if (Array.isArray(this.currentCoi.insuranceProvider) &&
        this.currentCoi.insuranceProvider.length > 0) {
      this.insuranceProviderName = this.currentCoi.insuranceProvider
        .map(provider => this.extractDisplayName(provider, 'name'))
        .join(', ');
    } else {
      this.insuranceProviderName = this.extractDisplayName(
        this.currentCoi.insuranceProvider,
        'name'
      );
    }

    // Hydrate legal entities from cache
    if (Array.isArray(this.currentCoi.legalEntities) &&
        this.currentCoi.legalEntities.length > 0) {
      this.hydrateLegalEntities(this.currentCoi.legalEntities);
    }

    // Handle users/people
    this.primaryOwnerName = this.extractDisplayName(
      this.currentCoi.primaryOwner,
      'friendlyName',
      'email'
    );

    this.coiCoordinatorName = this.extractDisplayName(
      this.currentCoi.coiCoordinator,
      'friendlyName',
      'email'
    );

    this.internalSignatoryName = this.extractDisplayName(
      this.currentCoi.internalSignatory,
      'friendlyName',
      'email'
    );

    this.internalCoordinatorName = this.extractDisplayName(
      this.currentCoi.internalCoordinator,
      'friendlyName',
      'email'
    );

    this.secondaryOwnerName = this.extractDisplayName(
      this.currentCoi.secondaryOwner,
      'friendlyName',
      'email'
    );

    // Load document using base class method
    if (this.currentCoi.linkToCoi) {
      this.loadDocument(this.currentCoi.linkToCoi);
    }

    // Load critical dates associated with this COI by entityId
    if (this.currentCoi.coiId) {
      this.criticalDatesService.getDatesByEntityId(this.currentCoi.coiId)
        .subscribe(dates => {
          this.entityDates = dates;
        });
    } else {
      this.entityDates = [];
    }

    // Load attachments associated with this COI
    // NEW API (Phase 1): Query by parentEntityId, with fallback to legacy attachment IDs array
    if (this.currentCoi.coiId) {
      this.attachmentsService.getAttachmentsForEntity(this.currentCoi.coiId, 'Coi')
        .subscribe(attachments => {
          if (attachments && attachments.length > 0) {
            this.entityAttachments = attachments;
          } else if (this.currentCoi?.attachments?.length) {
            // Fallback: ETL'd COIs may not have parentEntityId on attachments
            this.attachmentsService.getAttachmentsByNumbers(this.currentCoi.attachments)
              .subscribe(legacyAttachments => {
                this.entityAttachments = legacyAttachments || [];
                this.cdr.detectChanges();
              });
          } else {
            this.entityAttachments = [];
          }
          this.cdr.detectChanges();
        });
    } else {
      this.entityAttachments = [];
    }
  }

  private extractDisplayName(
    value: any,
    primaryKey: string,
    fallbackKey?: string
  ): string {
    if (!value) return 'N/A';

    if (typeof value === 'string') {
      return value;
    }

    if (typeof value === 'object') {
      if (value[primaryKey]) return value[primaryKey];
      if (fallbackKey && value[fallbackKey]) return value[fallbackKey];
    }

    return 'N/A';
  }

  /**
   * Hydrates legal entity IDs from the cache to get display names.
   * Handles both string IDs and already-hydrated objects.
   */
  private hydrateLegalEntities(legalEntities: any[]): void {
    const lookups = legalEntities.map(le => {
      // Already hydrated object
      if (typeof le === 'object' && le?.legalEntityName) {
        return of(le.legalEntityName);
      }
      // String ID — look up from cache
      const id = typeof le === 'string' ? le : le?.legalEntityNum;
      if (!id) return of(null);
      return this.legalEntityService.getLegalEntityFriendlyName(id);
    });

    forkJoin(lookups).subscribe(names => {
      this.legalEntitiesDisplay = names
        .filter(n => !!n)
        .join(', ') || 'N/A';
    });
  }

  editCoi() {
    if (this.currentCoi?.coiNum) {
      this.router.navigate(['/securehome/new-coi', this.currentCoi.coiNum]);
    }
  }

  goBack() {
    this.router.navigate(['/securehome/cois']);
  }

  formatDate(date: string | undefined): string {
    if (!date) return 'N/A';
    return new Date(date).toLocaleDateString();
  }

  // Tab selection handler
  selectTab(index: number): void {
    this.selectedTabIndex = index;
  }

  // Action methods
  addAttachment(): void {
    const entityObjectForDialog = {
      ...this.currentCoi,
      entityType: EntityType.Coi
    };

    const dialogRef = this.dialog.open(NewAttachmentComponent, {
      width: '500px',
      disableClose: true,
      data: {
        entityObject: entityObjectForDialog
      }
    });

    dialogRef.afterClosed().subscribe(newAttachment => {
      if (!this.currentCoi) return;

      if (newAttachment && newAttachment.attachmentNum) {
        if (this.currentCoi.coiId) {
          this.attachmentsService.getAttachmentsForEntity(this.currentCoi.coiId, 'Coi')
            .pipe(takeUntil(this.destroy$))
            .subscribe({
              next: (attachments) => {
                this.entityAttachments = attachments;
              },
              error: (err) => {
                console.error('Failed to fetch attachments:', err);
                this.entityAttachments = [...this.entityAttachments, newAttachment];
              }
            });
        }
      }
    });
  }

  addCriticalDate(): void {
    // Use the base class method from ViewEntity (call via super to avoid recursion)
    super['addCriticalDate'](
      this.currentCoi,
      'coiNum',
      (id: string, update: any) => {
        // Extract the attribute name and value from the update object
        const attribute = Object.keys(update)[0];
        const value = update[attribute];
        return this.coiService.updateCoiAttribute(id, attribute, value);
      }
    );
  }

  refreshAttachments(): void {
    if (this.currentCoi?.coiId) {
      this.attachmentsService.getAttachmentsForEntity(this.currentCoi.coiId, 'Coi')
        .pipe(takeUntil(this.destroy$))
        .subscribe(attachments => {
          if (attachments && attachments.length > 0) {
            this.entityAttachments = attachments;
          } else if (this.currentCoi?.attachments?.length) {
            this.attachmentsService.getAttachmentsByNumbers(this.currentCoi.attachments)
              .pipe(takeUntil(this.destroy$))
              .subscribe(legacyAttachments => {
                this.entityAttachments = legacyAttachments || [];
              });
          } else {
            this.entityAttachments = [];
          }
        });
    }
  }

  refreshCriticalDates(): void {
    if (this.currentCoi?.coiId) {
      this.criticalDatesService.getDatesByEntityId(this.currentCoi.coiId)
        .pipe(takeUntil(this.destroy$))
        .subscribe(dates => {
          this.entityDates = dates;
        });
    }
  }

  saveNotes(notes: NoteRecord[]): void {
    if (!this.currentCoi) return;
    this.currentCoi.notes = notes;
    this.coiService.writeCoi(this.currentCoi)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        error: (err) => console.error('Failed to save notes:', err)
      });
  }
}
